import { PureComponent } from 'react';
import T from 'prop-types';
import { markerPropType } from '../../../../../../../customPropTypes';
import { getMarkerEndpoints } from '../../../../../../../shared/helpers/markers';

const deriveStateFromProps = ({ marker, markerWidth, episodeDuration, pixelsPerSecond }) => ({
  markerOffset: (marker.start / (episodeDuration || 1)) * markerWidth,
  startSecond: marker.start,
  startPosition: marker.start * pixelsPerSecond,
});

export default class DragAndDrop extends PureComponent {
  constructor(props) {
    super(props);
    this.state = deriveStateFromProps(props);
    this.state.mouseOffsetLeftInMarker = 0;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState(deriveStateFromProps(nextProps));
  }

  componentWillUnmount() {
    this.removeDocumentListeners();
  }

  addDocumentListeners() {
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  }

  removeDocumentListeners() {
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  }

  calculateMarkerOffsetFromMousePosition(mouseOffsetLeft) {
    const {
      width: timelineWidth = 1,
      left: timelineOffsetLeft = 0,
    } = this.markerRef.offsetParent.getBoundingClientRect();
    const mouseOffsetLeftInTimeline = mouseOffsetLeft - Math.round(timelineOffsetLeft);
    const offset = (mouseOffsetLeftInTimeline / timelineWidth) * this.props.markerWidth;

    return Math.min(this.props.markerWidth, Math.max(0, offset));
  }

  clampSecondsToEpisode(requestedStart) {
    const { marker, markers, episodeDuration } = this.props;
    const [minStart, maxStart] = getMarkerEndpoints(markers, marker.id, episodeDuration);

    return Math.min(maxStart, Math.max(minStart, requestedStart));
  }

  calculateStart(mouseOffsetLeft, markerOffset) {
    const { mouseOffsetLeftInMarker = 0 } = this.state;
    const { left: timelineOffsetLeft } = this.markerRef.offsetParent.getBoundingClientRect();
    const mouseOffsetLeftInTimeline = mouseOffsetLeft - Math.round(timelineOffsetLeft);
    const offset = mouseOffsetLeftInTimeline - mouseOffsetLeftInMarker + markerOffset;

    return this.clampSecondsToEpisode(offset / this.props.pixelsPerSecond);
  }

  handleMouseDown = event => {
    event.preventDefault();
    event.stopPropagation();

    const { left: timelineOffsetLeft = 0 } = this.markerRef.getBoundingClientRect();
    const mouseOffsetLeftInMarker = event.clientX - Math.round(timelineOffsetLeft);
    this.setState({ mouseOffsetLeftInMarker });
    this.props.onSelect(this.props.marker.id);
    this.addDocumentListeners();
  };

  handleMouseMove = event => {
    const markerOffset = this.calculateMarkerOffsetFromMousePosition(event.clientX);
    const startSecond = this.calculateStart(event.clientX, markerOffset);

    this.setState({ startSecond, markerOffset });
  };

  handleMouseUp = () => {
    this.setState({ mouseOffsetLeftInMarker: 0 });
    if (this.state.startSecond !== this.props.marker.start) {
      this.props.onChange(this.props.marker.id, this.state.startSecond);
    }

    this.removeDocumentListeners();
  };

  setRef = element => {
    this.markerRef = element;
  };

  render() {
    const { pixelsPerSecond } = this.props;
    const { startSecond, markerOffset } = this.state;

    return this.props.children({
      setRef: this.setRef,
      markerOffsetLeft: startSecond * pixelsPerSecond - markerOffset,
      anchorLineOffsetLeft: startSecond * pixelsPerSecond,
      onMouseDown: this.handleMouseDown,
    });
  }
}

DragAndDrop.defaultProps = {
  episodeDuration: 0,
};

DragAndDrop.propTypes = {
  marker: markerPropType.isRequired,
  markers: T.arrayOf(markerPropType).isRequired,
  markerWidth: T.number.isRequired,
  pixelsPerSecond: T.number.isRequired,
  onSelect: T.func.isRequired,
  onChange: T.func.isRequired,
  episodeDuration: T.number,
};
